import {
    ChangeDetectionStrategy,
    Component,
    input,
    output,
    signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

export type AncFormAlertVariant = 'success' | 'error' | 'warning' | 'info';

/**
 * Inline status banner for form-level feedback — submit success, server/API
 * errors, and warnings — covering the states the legacy create-order/shipment
 * forms were missing. Stateless apart from the local dismissed flag.
 *
 * @example
 * <lib-anc-form-alert
 *     *ngIf="saveError"
 *     [variant]="'error'"
 *     [dismissible]="true"
 *     (dismissed)="saveError = ''">
 *   {{ saveError }}
 * </lib-anc-form-alert>
 */
@Component({
    selector: 'lib-anc-form-alert',
    imports: [CommonModule, TranslateModule],
    templateUrl: './anc-form-alert.component.html',
    styleUrls: ['./anc-form-alert.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AncFormAlertComponent {
    /** Visual tone — drives colour and the default leading icon. */
    variant = input<AncFormAlertVariant>('info');
    /** Show a close button. */
    dismissible = input<boolean>(false);
    /** Optional icon-font class override; falls back to a per-variant default. */
    icon = input<string>('');

    /** Emitted when the user dismisses the banner. */
    dismissed = output<void>();

    protected readonly closed = signal(false);

    private readonly defaultIcons: Record<AncFormAlertVariant, string> = {
        success: 'icon-check-circle',
        error: 'icon-alert-octagon',
        warning: 'icon-alert-triangle',
        info: 'icon-info'
    };

    protected iconClass(): string {
        return this.icon() || this.defaultIcons[this.variant()];
    }

    protected dismiss(): void {
        this.closed.set(true);
        this.dismissed.emit();
    }
}
